import React from "react";
import { Link } from "react-router-dom";
import { BOOKING_URL } from "@/lib/site";
import { BookLink } from "@/components/site/BookLink";
import { PageHero, Eyebrow } from "@/components/site/Section";
import { useContent } from "@/context/ContentContext";
import { Check } from "lucide-react";

const PLANS = [
  {
    icon: "🌸", label: "Foundational Program", name: "Sculpt Essence", price: "$175", featured: false,
    desc: "Our foundational program for those starting their wellness journey.",
    features: [
      "Semaglutide starting doses up to 0.5 mg/week",
      "Tirzepatide option: $275/month, up to 5 mg/week",
      "Weekly face-to-face or virtual provider check-ins",
      "Personalized nutrition and lifestyle coaching",
      "10% off select aesthetic treatments",
      "Standard medical supplies included",
    ],
  },
  {
    icon: "💎", label: "Advanced Wellness", name: "Sculpt Élite", price: "$249", featured: true,
    desc: "Advanced body sculpting, skin maintenance, and enhanced wellness support.",
    features: [
      "Semaglutide mid doses up to 1.5 mg/week",
      "Tirzepatide option: $349/month, up to 10 mg/week",
      "One complimentary quarterly RF tightening facial",
      "Monthly medical-grade body-composition tracking",
      "One complimentary monthly wellness injection (B12 / Lipo-C)",
      "Weekly provider check-ins and lifestyle guidance",
      "10% off select treatments + VIP member events",
    ],
  },
  {
    icon: "🚀", label: "All-Inclusive VIP", name: "Ascend Sculpting VIP", price: "$349", featured: false,
    desc: "The ultimate all-inclusive transformation package for maximum support and results.",
    features: [
      "Semaglutide maximum doses up to 2.5 mg/week",
      "Tirzepatide option: $449/month, up to 15 mg/week",
      "Weekly small-group training sessions with an expert trainer",
      "Premium sculpting and wellness benefits",
      "Weekly check-ins + ongoing body-composition tracking",
      "10% off all aesthetic treatments + exclusive VIP events",
    ],
  },
];

export default function GLP1() {
  const { glp = PLANS } = useContent();
  return (
    <div data-testid="glp1-page">
      <PageHero
        eyebrow="Seraphina Sculpt"
        title="Medical Weight Management & Foundational Wellness"
        subtitle="Achieve your health goals through medically supervised weight-management memberships with dedicated clinical support, personalized guidance, and exclusive member benefits."
      />

      <section className="py-14 bg-[var(--cream)]">
        <div className="max-w-[980px] mx-auto px-5 lg:px-10 text-center">
          <p className="font-display text-2xl sm:text-3xl text-[var(--charcoal)] leading-relaxed">
            We combine premium medical weight management options, including semaglutide and tirzepatide, with
            personalized wellness guidance, body-composition support, and medical-grade skin-tightening benefits.
          </p>
          <p className="text-sm text-[var(--charcoal-soft)] mt-6 max-w-[790px] mx-auto leading-relaxed">
            Membership and medication eligibility are determined during a clinical consultation. Prescription
            treatment is provided only when medically appropriate. Tirzepatide is available for $100 more per month
            in each membership tier.
          </p>
        </div>
      </section>

      <section className="pb-20 bg-[var(--cream)]">
        <div className="max-w-[1220px] mx-auto px-5 lg:px-10 grid lg:grid-cols-3 gap-7 items-stretch">
          {glp.map((p) => (
            <div
              key={p.name}
              data-testid={`glp-plan-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`relative soft-card rounded-3xl p-9 flex flex-col ${p.featured ? "border-2 border-[var(--gold)] lg:-translate-y-3 shadow-xl" : ""}`}
            >
              {p.featured && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[var(--gold)] text-white text-[0.65rem] tracking-widest font-bold rounded-full px-5 py-2">
                  MOST POPULAR
                </span>
              )}
              <div className="text-4xl mb-4">{p.icon}</div>
              <p className="eyebrow mb-2">{p.label}</p>
              <h2 className="font-display text-4xl text-[var(--charcoal)]">{p.name}</h2>
              <p className="mt-3 text-[var(--charcoal-soft)]">
                <span className="font-display text-4xl text-[var(--charcoal)]">{p.price}</span> / month
              </p>
              <p className="italic text-sm text-[var(--charcoal-soft)] mt-3 min-h-[56px]">{p.desc}</p>
              <ul className="mt-5 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-[var(--charcoal)]">
                    <span className="mt-0.5 text-[var(--gold)]"><Check size={16} /></span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <BookLink label={`GLP-1 ${p.name}`} href={BOOKING_URL} className="btn-gold rounded-md w-full py-3.5 mt-7 text-center">
                Book a Consultation
              </BookLink>
            </div>
          ))}
        </div>

        <div className="max-w-[980px] mx-auto px-5 lg:px-10 mt-14">
          <div className="soft-card rounded-2xl p-8 flex items-start gap-5 bg-[var(--cream-2)]">
            <span className="text-3xl text-[var(--gold)]">✦</span>
            <div>
              <h3 className="font-display text-2xl text-[var(--charcoal)]">Personalized Care, Limited Enrollment</h3>
              <p className="text-sm text-[var(--charcoal-soft)] mt-1 leading-relaxed">
                Spots in our medical weight-management programs are limited to help ensure attentive, high-quality
                care for every member.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/peptides" className="btn-outline-gold rounded-md px-7 py-3.5">Peptide Therapy</Link>
            <Link to="/contact" className="btn-outline-gold rounded-md px-7 py-3.5">Contact Us</Link>
          </div>
        </div>
      </section>

      <section className="py-14 bg-[var(--cream-2)] border-y border-[var(--line)]">
        <div className="max-w-[1120px] mx-auto px-5 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <Eyebrow className="mb-2">Payment Plans Available</Eyebrow>
            <h2 className="font-display text-3xl text-[var(--charcoal)]">Cherry Financing</h2>
            <p className="text-[var(--charcoal-soft)] mt-1">Explore Cherry payment plans for eligible treatments and wellness services.</p>
          </div>
          <Link to="/financing" className="btn-gold rounded-md px-8 py-3.5 whitespace-nowrap">View Financing</Link>
        </div>
      </section>
    </div>
  );
}
